import { Suspense, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../platform/auth/AuthContext';
import { ViewLoader } from './ViewLoader';
import { NativeBridge } from '../services/NativeBridge';
import { isAppView } from './navigation';
import { lazyChunk } from '../utils/lazyChunk';

const MessagesSidePanel = lazyChunk(
  () => import('../features/messages/MessagesSidePanel'),
  (module) => module.MessagesSidePanel,
);
const NotificationsSidePanel = lazyChunk(
  () => import('../features/notifications/NotificationsSidePanel'),
  (module) => module.NotificationsSidePanel,
);

type NativeSidePanel = 'messages' | 'notifications';

interface NativeSidePanelAppProps {
  initialPanel: NativeSidePanel;
  initialChatId?: string | null;
}

function closeNativeSidePanel(panel: NativeSidePanel) {
  NativeBridge.send('panelState', {
    messagesOpen: panel === 'messages' ? false : undefined,
    notificationsOpen: panel === 'notifications' ? false : undefined,
  });
}

function sendPanelNavigationDetail(detail: Record<string, any>) {
  NativeBridge.send('panelAction', {
    kind: 'appNavigate',
    detail,
  });
}

export function NativeSidePanelApp({
  initialPanel,
  initialChatId = null,
}: NativeSidePanelAppProps) {
  const auth = useAuth();
  const [activePanel, setActivePanel] = useState<NativeSidePanel>(initialPanel);
  const [chatId, setChatId] = useState<string | null>(initialChatId);

  useEffect(() => {
    return NativeBridge.on('panel', ({ panel, chatId: nextChatId }: { panel: string; chatId?: string }) => {
      if (panel === 'messages' || panel === 'notifications') {
        setActivePanel(panel);
        setChatId(typeof nextChatId === 'string' && nextChatId.length > 0 ? nextChatId : null);
      }
    });
  }, []);

  const panelContent = useMemo(() => {
    if (auth.status === 'unknown') return <ViewLoader />;
    if (!auth.user || auth.status !== 'authenticated') return <ViewLoader />;

    if (activePanel === 'notifications') {
      return (
        <NotificationsSidePanel
          isOpen
          onClose={() => closeNativeSidePanel('notifications')}
          onNavigate={(url, metadata) => {
            if (metadata?.chatId) {
              const nextChatId = String(metadata.chatId);
              setActivePanel('messages');
              setChatId(nextChatId);
              NativeBridge.send('panelAction', {
                kind: 'openPanel',
                panel: 'messages',
                chatId: nextChatId,
              });
              return;
            }

            if (metadata?.taskId) {
              sendPanelNavigationDetail({
                view: 'calendar',
                taskId: String(metadata.taskId),
              });
              return;
            }

            if (metadata?.projectId) {
              sendPanelNavigationDetail({
                view: 'project-detail',
                projectId: String(metadata.projectId),
                projectName: metadata.projectName ? String(metadata.projectName) : 'Project',
                projectTab:
                  typeof metadata.projectTab === 'string' && metadata.projectTab.length > 0
                    ? metadata.projectTab
                    : undefined,
              });
              return;
            }

            if (metadata?.clientId) {
              sendPanelNavigationDetail({
                view: 'client-detail',
                clientId: String(metadata.clientId),
                clientName: metadata.clientName ? String(metadata.clientName) : 'Client',
              });
              return;
            }

            if (url && isAppView(url)) {
              sendPanelNavigationDetail({
                view: url,
              });
            }
          }}
        />
      );
    }

    return (
      <MessagesSidePanel
        onClose={() => closeNativeSidePanel('messages')}
        initialChatId={chatId}
      />
    );
  }, [activePanel, auth.status, auth.user, chatId]);

  return (
    <div
      className="h-screen w-screen overflow-hidden bg-background font-apple"
      data-native-side-panel={activePanel}
    >
      <Suspense fallback={<ViewLoader />}>{panelContent}</Suspense>
    </div>
  );
}

NativeSidePanelApp.displayName = 'NativeSidePanelApp';
